/**
 * Block User Modal
 * Modal for blocking/unblocking users
 */
import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { adminService, type AdminUser } from '../../services/AdminService';
import { useAdminModalHandler } from '../../hooks/useAdminModalHandler';

interface BlockUserModalProps {
  user: AdminUser;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const BlockUserModal: React.FC<BlockUserModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const { loading, handleSubmit } = useAdminModalHandler({ onSuccess, onClose });

  const isBlocking = !user.is_blocked;
  const action = isBlocking ? 'block' : 'unblock';

  const onSubmit = () => handleSubmit(
    () => adminService.blockUser(user.id, isBlocking, reason || undefined),
    `Successfully ${action}ed ${user.username}`,
    `Failed to ${action} user`
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isBlocking ? 'Block User' : 'Unblock User'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* User Info */}
            <VStack align="stretch" spacing={1}>
              <Text fontSize="sm" color="gray.600">
                User
              </Text>
              <Text fontWeight="medium">{user.username}</Text>
              <Text fontSize="sm" color="gray.500">
                {user.email}
              </Text>
            </VStack>

            {/* Warning */}
            {isBlocking ? (
              <Alert status="warning">
                <AlertIcon />
                <VStack align="stretch" spacing={1}>
                  <AlertTitle fontSize="sm">Block User</AlertTitle>
                  <AlertDescription fontSize="xs">
                    Blocking this user will:
                    <br />• Prevent them from logging in
                    <br />• Invalidate all their active sessions
                    <br />• Disable their invite codes
                    <br />• Prevent them from generating new invites
                  </AlertDescription>
                </VStack>
              </Alert>
            ) : (
              <Alert status="info">
                <AlertIcon />
                <VStack align="stretch" spacing={1}>
                  <AlertTitle fontSize="sm">Unblock User</AlertTitle>
                  <AlertDescription fontSize="xs">
                    Unblocking this user will:
                    <br />• Allow them to log in again
                    <br />• Restore their invite generation ability
                    <br />• Re-enable their existing invite codes
                  </AlertDescription>
                </VStack>
              </Alert>
            )}

            {/* Reason (optional for blocking) */}
            {isBlocking && (
              <FormControl>
                <FormLabel>
                  Reason <Text as="span" fontSize="sm" color="gray.500">(Optional)</Text>
                </FormLabel>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason for blocking this user..."
                  rows={3}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  This reason will be logged in the audit trail.
                </Text>
              </FormControl>
            )}

            {/* Confirmation Text */}
            <Text fontSize="sm" color="gray.600">
              Are you sure you want to {action} <strong>{user.username}</strong>?
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme={isBlocking ? 'red' : 'green'}
            onClick={onSubmit}
            isLoading={loading}
          >
            {isBlocking ? 'Block User' : 'Unblock User'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BlockUserModal;
